import {
  login as loginRequest,
  oauthLogin as oauthLoginRequest,
  fetchWalletNonce,
  resetForgotPassword,
  sendVerificationCode,
  verifyForgotPassword,
  walletLogin,
} from '~/api/login'
import type { LoginRequestDto, LoginResultDto, LoginType, OAuthProvider } from '~/types/api/login'
import { ApiError } from '~/types/api'
import { refreshPlayerMe, refreshKycStatus } from '~/utils/applyPlayerMe'

/** EIP-1193 钱包注入提供者（MetaMask 等）；仅声明登录链路用到的能力 */
interface EthereumProvider {
  isMetaMask?: boolean
  request: (args: { method: string; params?: unknown[] | object }) => Promise<unknown>
  /** accountsChanged 等事件订阅（EIP-1193）；缺失时跳过账号切换监听 */
  on?: (event: string, listener: (payload: unknown) => void) => void
  removeListener?: (event: string, listener: (payload: unknown) => void) => void
  /**
   * 多钱包共存时的 provider 数组（MetaMask EIP-5749 约定）：
   * 装了 TronLink 等会抢占 window.ethereum 的钱包时，真正的 MetaMask 在此数组中。
   */
  providers?: EthereumProvider[]
}

/** 浏览器中 TronLink 注入的标记（用于识别它对 window.ethereum 的抢占/伪装） */
const hasTronLink = (): boolean => {
  if (!import.meta.client) return false
  const w = window as unknown as { tronWeb?: unknown; tronLink?: unknown }
  return !!(w.tronWeb || w.tronLink)
}

/**
 * 从多钱包数组 providers 中找 MetaMask（EIP-5749）。
 * 数组条目可信度高于 window.ethereum 本体；TronLink 抢占时真身常在此数组。
 */
const findMetaMaskInProviders = (): EthereumProvider | null => {
  const ethereum = (window as unknown as { ethereum?: EthereumProvider }).ethereum
  if (!Array.isArray(ethereum?.providers)) return null
  // 递归一层，兼容非标准嵌套；去重
  const queue: (EthereumProvider | undefined)[] = [...ethereum.providers]
  const seen = new Set<EthereumProvider>()
  while (queue.length) {
    const cur = queue.shift()
    if (!cur || seen.has(cur)) continue
    seen.add(cur)
    if (cur.isMetaMask) return cur
    if (Array.isArray(cur.providers)) queue.push(...cur.providers)
  }
  return null
}

/**
 * 无 providers 数组时的直接读取：仅在没有 TronLink 抢占痕迹时信任。
 * TronLink 存在且 window.ethereum 自称 MetaMask 时视为伪装，返回 null。
 */
const getBareMetaMask = (): EthereumProvider | null => {
  const ethereum = (window as unknown as { ethereum?: EthereumProvider }).ethereum
  if (!ethereum || Array.isArray(ethereum.providers)) return null
  if (!ethereum.isMetaMask) return null
  return hasTronLink() ? null : ethereum
}

/** EIP-6963 钱包广播条目（多钱包发现标准，独立于 window.ethereum） */
interface EIP6963ProviderDetail {
  info: { name?: string; rdns?: string }
  provider: EthereumProvider
}

/**
 * EIP-6963 发现：广播 requestProvider 后收集 announceProvider，返回所有条目。
 * 独立于 window.ethereum，可用于它被 TronLink 完全抢占/伪装的环境。
 */
const discoverWalletsViaEIP6963 = (timeoutMs = 500): Promise<EIP6963ProviderDetail[]> => {
  return new Promise(resolve => {
    if (!import.meta.client) return resolve([])
    const list: EIP6963ProviderDetail[] = []
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      window.removeEventListener('eip6963:announceProvider', onAnnounce)
      clearTimeout(timer)
      resolve(list)
    }
    const onAnnounce = (e: Event) => {
      const detail = (e as CustomEvent<EIP6963ProviderDetail>).detail
      if (detail?.provider) list.push(detail)
    }
    window.addEventListener('eip6963:announceProvider', onAnnounce)
    window.dispatchEvent(new Event('eip6963:requestProvider'))
    const timer = setTimeout(finish, timeoutMs)
  })
}

/**
 * 按可信度优先级解析 MetaMask 真身：
 * 1) EIP-6963 中 rdns=io.metamask（身份最强，TronLink 无法冒充 rdns）
 * 2) providers 数组中的 isMetaMask
 * 3) 无 TronLink 痕迹时，window.ethereum 本体 isMetaMask
 * 同时输出诊断信息，便于排查环境问题。
 */
const resolveMetaMask = async (): Promise<EthereumProvider | null> => {
  const announced = await discoverWalletsViaEIP6963()
  const real = announced.find(d => d.info?.rdns === 'io.metamask')
  if (real) {
    console.info('[auth] MetaMask via EIP-6963')
    return real.provider
  }

  const inProviders = findMetaMaskInProviders()
  if (inProviders) {
    console.info('[auth] MetaMask via window.ethereum.providers')
    return inProviders
  }

  const bare = getBareMetaMask()
  if (bare) {
    console.info('[auth] MetaMask via window.ethereum')
    return bare
  }

  console.warn('[auth] MetaMask not resolved', {
    hasEthereum: !!(window as unknown as { ethereum?: EthereumProvider }).ethereum,
    ethereumIsMetaMask: (window as unknown as { ethereum?: EthereumProvider }).ethereum?.isMetaMask,
    hasProvidersArray: Array.isArray(
      (window as unknown as { ethereum?: EthereumProvider }).ethereum?.providers,
    ),
    announced: announced.map(d => ({
      name: d.info?.name,
      rdns: d.info?.rdns,
      isMetaMask: !!d.provider.isMetaMask,
    })),
    hasTronLink: hasTronLink(),
  })
  return null
}

/** 钱包 RPC 错误（MetaMask 拒绝授权 / 签名时抛出，EIP-1193 标准 code=4001） */
interface ProviderRpcError extends Error {
  code: number
}

const isProviderReject = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  (error as ProviderRpcError).code === 4001

/** MetaMask 已有待处理的连接请求（-32002：弹窗被关掉但未确认/拒绝时，后续请求会被拒） */
const isRequestPending = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  (error as ProviderRpcError).code === -32002

export interface LoginPayload {
  type: LoginType
  /** 用户名 / 邮箱 / 手机号 */
  account: string
  /** 用户名登录时必填 */
  password?: string
  /** 邮箱 / 手机号登录时必填 */
  code?: string
}

export interface VerifyResetCodePayload {
  /** 账号（用户名） */
  account: string
  /** 已绑定的手机 / 邮箱 */
  contact: string
  code: string
}

export interface ConfirmResetPasswordPayload {
  /** 账号（用户名） */
  account: string
  /** 已绑定的手机 / 邮箱（与步骤一保持一致） */
  contact: string
  /** 发往 contact 的 6 位验证码（本次请求将消费） */
  code: string
  newPassword: string
  confirmPassword: string
}

/**
 * 登录注册业务层：集中管理登录 / 发码 / 找回密码的 API 调用与副作用。
 * 登录已接入真实接口（POST /account/login）；发码 / 找回密码仍为 mock，接入时替换各方法内的 $fetch 逻辑。
 */
export const useAuth = () => {
  const userStore = useUserStore()
  const { refreshWallet } = useWallet()
  // 必须在 composable 顶层取：异步方法体内调用 useI18n 已脱离 setup 上下文
  const { t } = useI18n()

  /** 登录 / 注册（新用户自动创建账号） */
  const login = async (payload: LoginPayload): Promise<void> => {
    // 重登 / 换号：先清旧会话，避免过期 token 影响登录接口
    userStore.clearSession('manual')

    const referralCode = getReferralCode()
    const dto: LoginRequestDto = {
      type: payload.type,
      username: payload.account,
      password: (payload.type === 'username' ? payload.password : payload.code) ?? '',
      // 后端仅对新账号生效，失效一律忽略不报错
      ...(referralCode ? { referral_code: referralCode } : {}),
    }
    const result = await loginRequest(dto)

    await applyLoginResult(result, {
      isPassword: payload.type === 'username',
      ...(payload.type === 'email' ? { email: payload.account } : {}),
      ...(payload.type === 'phone' ? { phone: payload.account } : {}),
    })
  }

  /** 落库登录态并补全档案 / 余额（账密与第三方登录共用） */
  const applyLoginResult = async (
    result: LoginResultDto,
    profile: { isPassword: boolean; email?: string; phone?: string },
  ): Promise<void> => {
    // 防御：token 缺失时不得落「已登录但无凭证」半态（后续请求必 401 清会话）
    if (!result?.access_token) {
      throw new ApiError(t('login.errorLoginFailed'), { code: -1 })
    }
    userStore.setToken(result.access_token)
    userStore.setProfile({ uid: result.player_id, ...profile })
    userStore.setLoggedIn(true)
    console.info('[auth] login applied, token …' + result.access_token.slice(-8))

    // 档案、余额、KYC 状态并行补全；失败不影响登录态
    const [meResult, walletResult, kycResult] = await Promise.allSettled([
      refreshPlayerMe({ trustFundPasswordFromMe: true }),
      refreshWallet(),
      refreshKycStatus(),
    ])
    if (meResult.status === 'rejected') {
      console.error('[auth] fetchMe after login failed:', meResult.reason)
    }
    if (walletResult.status === 'rejected') {
      console.error('[auth] refreshWallet after login failed:', walletResult.reason)
    }
    if (kycResult.status === 'rejected') {
      console.error('[auth] fetchKycStatus after login failed:', kycResult.reason)
    }

    // 推广码一次性：登录成功（新账号绑定或老账号忽略）后即清除
    clearReferralCode()
  }

  /** 第三方快捷登录：渠道凭据换 JWT，副作用同账密登录 */
  const oauthLogin = async (provider: OAuthProvider, credential: string): Promise<void> => {
    userStore.clearSession('manual')
    const referralCode = getReferralCode()
    const result = await oauthLoginRequest(provider, {
      credential,
      ...(referralCode ? { referral_code: referralCode } : {}),
    })
    await applyLoginResult(result, {
      isPassword: false,
      ...(result.email ? { email: result.email } : {}),
    })
  }

  /**
   * MetaMask（EIP-1193 注入钱包）签名登录，SIWE 流程：
   * 1) eth_requestAccounts 连接拿地址
   * 2) POST /account/wallet/nonce 申请挑战文本（绑定 Origin，5 分钟一次性）
   * 3) personal_sign 原样签名挑战文本（params 顺序 [message, address]）
   * 4) POST /account/wallet/login 提交地址 / 签名验签换 JWT
   * 挑战过期（1023）自动重新申请一次；钱包切换账号（accountsChanged）作废旧挑战并中断。
   */
  const metamaskLogin = async (): Promise<void> => {
    // 按可信度优先级解析 MetaMask 真身（EIP-6963 → providers 数组 → 本体），
    // 避免被 TronLink 抢占/伪装的 window.ethereum 截胡
    const provider = await resolveMetaMask()
    if (!provider) {
      throw new Error(t('login.metamaskNotInstalled'))
    }

    // 1) 连接钱包获取地址
    let address: string
    try {
      const accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[]
      address = accounts[0] ?? ''
    } catch (error) {
      if (isProviderReject(error)) {
        throw new Error(t('login.metamaskRejected'), { cause: error })
      }
      if (isRequestPending(error)) {
        throw new Error(t('login.metamaskRequestPending'), { cause: error })
      }
      // 把原始错误输出到控制台，方便调试其他未知错误码
      console.error('[auth] eth_requestAccounts failed:', error)
      throw new Error(t('login.metamaskConnectFailed'), { cause: error })
    }
    if (!address) throw new Error(t('login.metamaskConnectFailed'))

    // 重登 / 换号：清旧会话，避免过期 token 影响后续接口
    userStore.clearSession('manual')

    // 监听钱包内切换账号：切换后挑战作废（签名地址与申请地址不一致后端会拒绝）
    let accountSwitched = false
    const onAccountsChanged = (payload: unknown) => {
      const current = Array.isArray(payload) ? (payload[0] as string | undefined) : undefined
      if (!current || current.toLowerCase() !== address.toLowerCase()) accountSwitched = true
    }
    provider.on?.('accountsChanged', onAccountsChanged)

    /** 挑战 → 签名 → 提交；每一步前检查账号是否已切换 */
    const signAndLogin = async (): Promise<LoginResultDto> => {
      if (accountSwitched) throw new Error(t('login.metamaskAccountChanged'))

      const { message } = await fetchWalletNonce({ address })

      if (accountSwitched) throw new Error(t('login.metamaskAccountChanged'))
      let signature: string
      try {
        signature = (await provider.request({
          method: 'personal_sign',
          params: [message, address],
        })) as string
      } catch (error) {
        if (isProviderReject(error)) {
          throw new Error(t('login.metamaskRejected'), { cause: error })
        }
        throw new Error(t('login.errorLoginFailed'), { cause: error })
      }

      if (accountSwitched) throw new Error(t('login.metamaskAccountChanged'))
      return await walletLogin({ address, signature })
    }

    try {
      let result: LoginResultDto
      try {
        result = await signAndLogin()
      } catch (error) {
        if (error instanceof ApiError && error.code === 1023) {
          // 挑战不存在或已过期：自动重新申请一次
          result = await signAndLogin()
        } else if (error instanceof ApiError && [1014, 1016, 1017, 1025].includes(error.code)) {
          // 签名失败过多 / 建号限流 / 频控存储不可用 / 申请过频
          throw new Error(t('login.metamaskRateLimited'), { cause: error })
        } else if (error instanceof ApiError && (error.status === 403 || error.code === 403)) {
          throw new Error(t('login.metamaskBanned'), { cause: error })
        } else {
          throw error
        }
      }
      await applyLoginResult(result, { isPassword: false })
    } finally {
      provider.removeListener?.('accountsChanged', onAccountsChanged)
    }
  }

  /** 退出登录：清鉴权与登录后拉取的会话数据 */
  const logout = () => {
    userStore.clearSession('manual')
  }

  /** 登录页发送邮箱 / 短信验证码 */
  const sendLoginCode = async (type: 'email' | 'phone', account: string): Promise<void> => {
    await sendVerificationCode({ type, target: account })
  }

  /** 找回密码页发送验证码到已绑定的手机 / 邮箱（purpose=reset，缺省 login 的码在 verify/reset 校验不过） */
  const sendResetCode = async (contact: string): Promise<void> => {
    await sendVerificationCode({
      type: isEmail(contact) ? 'email' : 'phone',
      target: contact,
      purpose: 'reset',
    })
  }

  /** 找回密码第一步：校验账号 + 已绑定手机/邮箱 + 验证码（不消费验证码） */
  const verifyResetCode = async (payload: VerifyResetCodePayload): Promise<void> => {
    await verifyForgotPassword({
      username: payload.account,
      type: isEmail(payload.contact) ? 'email' : 'phone',
      target: payload.contact,
      code: payload.code,
    })
  }

  /** 找回密码第二步：确认设置新密码（消费验证码） */
  const confirmResetPassword = async (payload: ConfirmResetPasswordPayload): Promise<void> => {
    await resetForgotPassword({
      username: payload.account,
      type: isEmail(payload.contact) ? 'email' : 'phone',
      target: payload.contact,
      code: payload.code,
      new_password: payload.newPassword,
      confirm_password: payload.confirmPassword,
    })
  }

  /** 第三方登录 */
  const socialLogin = async (provider: string): Promise<void> => {
    // TODO: 替换为真实接口 / OAuth 跳转
    console.log('[auth] social login', provider)
  }

  return {
    login,
    oauthLogin,
    metamaskLogin,
    logout,
    sendLoginCode,
    sendResetCode,
    verifyResetCode,
    confirmResetPassword,
    socialLogin,
  }
}
